/**
 * Разбор ответов с ошибкой: создание карточек (`POST /cards/create`), ручная карточка
 * (`POST /cards/manual`), «Повторить» (`POST /cards/{id}/regenerate`) и словарный поиск
 * (`GET /cards/lookup`) — ТЗ главной § 10,
 * § 22.6, § 22.12. Чистые функции без axios и alias-импортов: их проверяют тесты
 * (`tests/home-composer.test.ts`). Тела ошибок интерцептор не преобразует — `details`
 * приходят в snake_case, как их пишет бэкенд (`daily_limit`, `reset_at`, `retry_after_ms`).
 * `status === null` — ответа нет (сеть, таймаут 15 с).
 */

export type CreateErrorCode =
  | 'ALREADY_ADDED'
  | 'DAILY_LIMIT_REACHED'
  | 'NOT_IN_DICTIONARY'
  // Для слова ещё идёт прошлая генерация — часть речи не поставлена, квота возвращена.
  | 'GENERATION_IN_PROGRESS'
  // Тот же запрос (id нажатия и пачка) ещё выполняется — исход проверяют по generation_id.
  | 'GENERATION_PART_IN_PROGRESS'
  | 'MODULE_NOT_FOUND'
  | 'EMAIL_NOT_VERIFIED'
  // 429 без кода — ограничитель частоты, а не жетоны.
  | 'RATE_LIMIT'
  | 'NETWORK'
  // 5xx: исход неизвестен.
  | 'SERVER'
  | 'UNKNOWN';

export type RegenerateErrorCode =
  | 'CARD_STATE_NOT_FOUND'
  | 'CARD_NOT_FOUND'
  | 'CARD_NOT_REGENERATABLE'
  // Словарный гейт до резерва: слова нет ни в словаре, ни в Яндексе — жетон не списан.
  | 'NOT_IN_DICTIONARY'
  | 'DAILY_LIMIT_REACHED'
  // «Повторить» карточки слова из игры бесплатен, но входит в суточный предохранитель ИИ-слов (429, 2026-11-03).
  | 'STORY_WORDS_AI_LIMIT'
  | 'RATE_LIMIT'
  | 'NETWORK'
  | 'SERVER'
  | 'UNKNOWN';

/** Детали дневной квоты из 429 `DAILY_GENERATION_LIMIT_REACHED`. */
export interface QuotaLimitInfo {
  limit: number;
  used: number;
  remaining: number;
  /** ISO-8601 UTC момента пополнения (00:00 МСК) или null, если бэк не прислал. */
  resetAt: string | null;
}

export interface CreateErrorModule {
  id: string;
  name: string;
}

export interface ParsedCreateError {
  code: CreateErrorCode;
  /** `error.code` сервера, если был. */
  serverCode: string | null;
  /** ALREADY_ADDED — модули ученика, где слово уже есть. */
  modules: CreateErrorModule[];
  /** DAILY_LIMIT_REACHED — лимит, использовано и время пополнения. */
  quota?: QuotaLimitInfo;
  /** GENERATION_PART_IN_PROGRESS — через сколько проверить, создались ли карточки. */
  retryAfterMs?: number;
}

export interface ParsedRegenerateError {
  code: RegenerateErrorCode;
  serverCode: string | null;
  quota?: QuotaLimitInfo;
}

/** Через сколько проверять исход, если сервер не подсказал `retry_after_ms`. */
export const DEFAULT_RETRY_AFTER_MS = 1500;

type Details = Record<string, unknown>;

function errorParts(body: unknown): { serverCode: string | null; details: Details } {
  const error = (body as { error?: { code?: unknown; details?: unknown } } | null)?.error;
  const serverCode = typeof error?.code === 'string' ? error.code : null;
  const details =
    error?.details && typeof error.details === 'object' && !Array.isArray(error.details)
      ? (error.details as Details)
      : {};
  return { serverCode, details };
}

const count = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/** Квота из `details` 429-ответа; деталей нет (старый бэк) — undefined. */
export function parseQuotaDetails(details: Details): QuotaLimitInfo | undefined {
  if (!Object.keys(details).length) return undefined;
  return {
    limit: count(details.daily_limit),
    used: count(details.used_today),
    remaining: count(details.remaining),
    resetAt: typeof details.reset_at === 'string' ? details.reset_at : null,
  };
}

/** `details.modules` 409-ответа (`[{id, name}]`); нет — пусто, текст будет общим. */
export function parseModulesDetails(details: Details): CreateErrorModule[] {
  const modules = details.modules;
  if (!Array.isArray(modules)) return [];
  return modules
    .filter(
      (m): m is { id: string; name: string } =>
        m != null &&
        typeof m === 'object' &&
        typeof (m as { id?: unknown }).id === 'string' &&
        typeof (m as { name?: unknown }).name === 'string',
    )
    .map((m) => ({ id: m.id, name: m.name }));
}

/** `details.retry_after_ms`; на всякий случай и camelCase; нет ни того, ни другого — 1,5 с. */
export function retryAfterMs(details: Details): number {
  for (const value of [details.retry_after_ms, details.retryAfterMs]) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  }
  return DEFAULT_RETRY_AFTER_MS;
}

const GENERATION_ID_CONFLICTS = new Set(['GENERATION_ID_CONFLICT', 'GENERATION_REQUEST_CONFLICT']);

/**
 * Ошибка создания карточек и ручной карточки. Коды `GENERATION_*` проверяются раньше, чем любой
 * 409 превращается в ALREADY_ADDED. Конфликт id нажатия — UNKNOWN: следующее нажатие уйдёт с
 * новым id. Ответ 200 с `replayed: true` — не ошибка: сервер повторил прежний ответ.
 */
export function parseCreateError(status: number | null, body: unknown): ParsedCreateError {
  const { serverCode, details } = errorParts(body);
  const base = { serverCode, modules: [] as CreateErrorModule[] };
  if (status === null) return { ...base, code: 'NETWORK' };
  if (status === 409) {
    if (serverCode === 'GENERATION_PART_IN_PROGRESS') {
      return { ...base, code: 'GENERATION_PART_IN_PROGRESS', retryAfterMs: retryAfterMs(details) };
    }
    if (serverCode && GENERATION_ID_CONFLICTS.has(serverCode)) return { ...base, code: 'UNKNOWN' };
    if (serverCode === 'CARD_GENERATION_IN_PROGRESS') return { ...base, code: 'GENERATION_IN_PROGRESS' };
    if (serverCode === 'MODULE_NOT_FOUND') return { ...base, code: 'MODULE_NOT_FOUND' };
    return { ...base, code: 'ALREADY_ADDED', modules: parseModulesDetails(details) };
  }
  if (status === 429) {
    return serverCode === 'DAILY_GENERATION_LIMIT_REACHED'
      ? { ...base, code: 'DAILY_LIMIT_REACHED', quota: parseQuotaDetails(details) }
      : { ...base, code: 'RATE_LIMIT' };
  }
  if (status === 403 && serverCode === 'EMAIL_NOT_VERIFIED') return { ...base, code: 'EMAIL_NOT_VERIFIED' };
  if (status === 400 && serverCode === 'NOT_IN_DICTIONARY') return { ...base, code: 'NOT_IN_DICTIONARY' };
  if (status >= 500) return { ...base, code: 'SERVER' };
  return { ...base, code: 'UNKNOWN' };
}

/**
 * Почему не ответил словарный поиск (`GET /cards/lookup`, ТЗ главной § 10): `offline` — браузер без
 * сети, `network` — запрос без ответа при живой сети; оба ждут возврата связи без «Повторить».
 * `rate_limit` — 429 ограничителя частоты, `email` — почта не подтверждена (403), `server` — 5xx.
 */
export type LookupErrorReason = 'offline' | 'network' | 'rate_limit' | 'email' | 'server' | 'unknown';

/** Ошибка словарного поиска → причина. `online` — `navigator.onLine` в момент ошибки. */
export function parseLookupError(status: number | null, body: unknown, online = true): LookupErrorReason {
  if (status === null) return online ? 'network' : 'offline';
  if (status === 429) return 'rate_limit';
  if (status === 403) return errorParts(body).serverCode === 'EMAIL_NOT_VERIFIED' ? 'email' : 'unknown';
  if (status >= 500) return 'server';
  return 'unknown';
}

const REGENERATE_CODES = new Set<RegenerateErrorCode>(['CARD_STATE_NOT_FOUND', 'CARD_NOT_FOUND', 'CARD_NOT_REGENERATABLE']);

/**
 * Ошибка «Повторить» (`regenerate`). 429 различаются по `error.code`: жетоны или ограничитель
 * частоты. NETWORK и SERVER — исход неизвестен: состав перечитывают, POST не повторяют.
 * 400 NOT_IN_DICTIONARY — словарный гейт до резерва (бэкенд 2026-10-20): жетон не списан.
 */
export function parseRegenerateError(status: number | null, body: unknown): ParsedRegenerateError {
  const { serverCode, details } = errorParts(body);
  if (status === null) return { code: 'NETWORK', serverCode };
  if (status === 429) {
    if (serverCode === 'STORY_WORDS_AI_LIMIT') return { code: 'STORY_WORDS_AI_LIMIT', serverCode };
    return serverCode === 'DAILY_GENERATION_LIMIT_REACHED'
      ? { code: 'DAILY_LIMIT_REACHED', serverCode, quota: parseQuotaDetails(details) }
      : { code: 'RATE_LIMIT', serverCode };
  }
  if (status === 400 && serverCode === 'NOT_IN_DICTIONARY') return { code: 'NOT_IN_DICTIONARY', serverCode };
  if (serverCode && REGENERATE_CODES.has(serverCode as RegenerateErrorCode)) {
    return { code: serverCode as RegenerateErrorCode, serverCode };
  }
  if (status >= 500) return { code: 'SERVER', serverCode };
  return { code: 'UNKNOWN', serverCode };
}
